import type { Editor } from "@tiptap/core";
import { EditorContent, useEditorState } from "@tiptap/react";
// @ts-ignore https://github.com/ueberdosis/tiptap/issues/7045
import { BubbleMenu } from "@tiptap/react/menus";
import cn from "classnames";
import { type ComponentProps, Fragment } from "react";
import type { IconType } from "react-icons";
import {
	TbBold,
	TbH1,
	TbH2,
	TbH3,
	TbH4,
	TbH5,
	TbItalic,
	TbList,
	TbListNumbers,
	TbStrikethrough,
} from "react-icons/tb";
import styles from "./MarkdownEditor.module.css";

interface MarkdownEditorProps extends ComponentProps<"div"> {
	editor: Editor;
}

interface MarkdownCommand {
	icon: IconType;
	enabled: (editor: Editor) => boolean; // Check if command is enabled
	active: (editor: Editor) => boolean; // Check if command is active
	run: (editor: Editor) => unknown;
}

const commands = {
	bold: {
		icon: TbBold,
		enabled: (editor) =>
			editor.extensionManager.extensions.some((x) => x.name === "bold"),
		active: (editor) => editor.isActive("bold"),
		run: (editor) => editor.commands.toggleBold(),
	},
	italic: {
		icon: TbItalic,
		enabled: (editor) =>
			editor.extensionManager.extensions.some((x) => x.name === "italic"),
		active: (editor) => editor.isActive("italic"),
		run: (editor) => editor.commands.toggleItalic(),
	},
	strike: {
		icon: TbStrikethrough,
		enabled: (editor) =>
			editor.extensionManager.extensions.some((x) => x.name === "strike"),
		active: (editor) => editor.isActive("strike"),
		run: (editor) => editor.commands.toggleStrike(),
	},
	bulletList: {
		icon: TbList,
		enabled: (editor) =>
			editor.extensionManager.extensions.some((x) => x.name === "bulletList"),
		active: (editor) => editor.isActive("bulletList"),
		run: (editor) => editor.commands.toggleBulletList(),
	},
	orderedList: {
		icon: TbListNumbers,
		enabled: (editor) =>
			editor.extensionManager.extensions.some((x) => x.name === "orderedList"),
		active: (editor) => editor.isActive("orderedList"),
		run: (editor) => editor.commands.toggleOrderedList(),
	},
	h1: {
		icon: TbH1,
		enabled: (editor) =>
			editor.extensionManager.extensions.some((x) => x.name === "heading"),
		active: (editor) => editor.isActive("heading", { level: 1 }),
		run: (editor) => editor.commands.toggleHeading({ level: 1 }),
	},
	h2: {
		icon: TbH2,
		enabled: (editor) =>
			editor.extensionManager.extensions.some((x) => x.name === "heading"),
		active: (editor) => editor.isActive("heading", { level: 2 }),
		run: (editor) => editor.commands.toggleHeading({ level: 2 }),
	},
	h3: {
		icon: TbH3,
		enabled: (editor) =>
			editor.extensionManager.extensions.some((x) => x.name === "heading"),
		active: (editor) => editor.isActive("heading", { level: 3 }),
		run: (editor) => editor.commands.toggleHeading({ level: 3 }),
	},
	h4: {
		icon: TbH4,
		enabled: (editor) =>
			editor.extensionManager.extensions.some((x) => x.name === "heading"),
		active: (editor) => editor.isActive("heading", { level: 4 }),
		run: (editor) => editor.commands.toggleHeading({ level: 4 }),
	},
	h5: {
		icon: TbH5,
		enabled: (editor) =>
			editor.extensionManager.extensions.some((x) => x.name === "heading"),
		active: (editor) => editor.isActive("heading", { level: 5 }),
		run: (editor) => editor.commands.toggleHeading({ level: 5 }),
	},
} as const satisfies Record<string, MarkdownCommand>;

const groups: (keyof typeof commands)[][] = [
	["bold", "italic", "strike"],
	["bulletList", "orderedList"],
	["h1", "h2", "h3", "h4", "h5"],
];

export function MarkdownEditor(props: MarkdownEditorProps) {
	const { editor } = props;

	return (
		<div>
			<EditorContent editor={editor} className={styles.editor} />
			<BubbleMenu
				editor={editor}
				className={styles.bubbleMenu}
				shouldShow={(state: { editor: Editor }) => state.editor.isFocused}
				options={{ placement: "bottom" }}
			>
				{groups.map((group, groupIndex) =>
					group.map((name, index) => (
						<Fragment key={name}>
							{groupIndex !== 0 && index === 0 && (
								<div className={styles.bubbleMenuDivider} />
							)}
							<BubbleMenuItem
								name={name as keyof typeof commands}
								editor={editor}
							/>
						</Fragment>
					)),
				)}
			</BubbleMenu>
		</div>
	);
}

interface BubbleMenuItemProps {
	name: keyof typeof commands;
	editor: Editor;
}

function BubbleMenuItem(props: BubbleMenuItemProps) {
	const { editor, name } = props;
	const command = commands[name];
	const isActive = useEditorState({
		editor: editor,
		selector: (state) => command.active(state.editor),
	});

	if (!command.enabled(editor)) {
		return null;
	}

	const Icon = command.icon;

	return (
		<button
			type="button"
			// onMouseDown: Prevents blur on the text selection
			onMouseDown={(e) => e.preventDefault()}
			onClick={() => command.run(editor)}
			className={cn(styles.bubbleButton, {
				[styles.bubbleButtonActive]: isActive,
			})}
		>
			<Icon size={18} />
		</button>
	);
}
